import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import '../calendar.css';
import { 
  Plus, 
  Clock, 
  User, 
  Users,
  MapPin, 
  FileText,
  X,
  Save,
  Calendar as CalendarIcon,
  AlertTriangle
} from 'lucide-react';

// Helper function to get status color
const getStatusColor = (status) => {
  const colors = {
    'available': '#22c55e', // success-500 (green)
    'booked': '#3b82f6', // primary-500 (blue)
    'confirmed': '#8b5cf6', // purple variant
    'in-progress': '#f59e0b', // warning-500 (orange)
    'completed': '#64748b', // secondary-500 (gray)
    'cancelled': '#ef4444', // danger-500 (red)
    'rescheduled': '#f97316' // orange variant
  };
  return colors[status] || '#64748b';
};

// Convert 24-hour time (HH:MM) to 12-hour format (h:mm AM/PM)
const formatTimeTo12Hour = (time24) => {
  if (!time24) return 'N/A';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const Calendar = () => {
  const { user, api } = useAuth();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showSchedulePreview, setShowSchedulePreview] = useState(false);
  const [selectedScheduleSlot, setSelectedScheduleSlot] = useState(null);
  const [students, setStudents] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedAppointmentEvent, setSelectedAppointmentEvent] = useState(null);
  const [bookingData, setBookingData] = useState({
    studentId: '',
    notes: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    start: '',
    end: '',
    studentId: '',
    subject: '',
    location: 'in-person',
    notes: '',
    teacherId: ''
  });
  const [rescheduleLimit, setRescheduleLimit] = useState(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleOptions, setRescheduleOptions] = useState([]);
  const [selectedRescheduleOption, setSelectedRescheduleOption] = useState(null);
  const [loadingRescheduleOptions, setLoadingRescheduleOptions] = useState(false);
  const [cancellationLimit, setCancellationLimit] = useState(null);
  const [appointmentsForTimeSlot, setAppointmentsForTimeSlot] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  // Fetch teachers for admin
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchTeachers();
    }
  }, [user]);

  // Fetch students for parents
  useEffect(() => {
    if (user?.role === 'parent') {
      fetchStudents();
    }
  }, [user]);

  // Auto-refresh calendar events periodically to reflect chronological status updates
  useEffect(() => {
    if (!calendarRef.current) return;

    let refreshInterval = null;

    // Wait for calendar to be initialized
    const checkCalendar = setInterval(() => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        clearInterval(checkCalendar);
        
        // Refresh every 2 minutes to update appointment statuses (in-progress, completed, etc.)
        refreshInterval = setInterval(() => {
          try {
            const view = calendarApi.view;
            if (view) {
              fetchEvents(view.activeStart, view.activeEnd);
            }
          } catch (error) {
            console.error('Error auto-refreshing calendar:', error);
          }
        }, 2 * 60 * 1000); // 2 minutes
      }
    }, 100);

    // Cleanup intervals on unmount
    return () => {
      clearInterval(checkCalendar);
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user]); // Re-run when user changes

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/calendar/teachers');
      setTeachers(response.data.teachers);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(response.data.students);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch all appointments for a specific time slot (for teachers)
  const fetchAppointmentsForTimeSlot = async (event) => {
    try {
      setLoadingAppointments(true);
      const dateStr = event.start.toISOString().split('T')[0];
      const startTime = event.start.toTimeString().split(' ')[0].substring(0, 5);
      const endTime = event.end.toTimeString().split(' ')[0].substring(0, 5);
      
      // Fetch appointments for this date and time range
      const startDate = new Date(dateStr + 'T00:00:00');
      const endDate = new Date(dateStr + 'T23:59:59');
      
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      
      const response = await api.get(`/appointments?${params}`);
      const allAppointments = response.data.appointments || [];
      
      // Filter appointments that overlap with this time slot
      const matchingAppointments = allAppointments.filter(apt => {
        const aptStart = apt.startTime.split(':').map(Number);
        const aptEnd = apt.endTime.split(':').map(Number);
        const slotStart = startTime.split(':').map(Number);
        const slotEnd = endTime.split(':').map(Number);
        
        const aptStartMinutes = aptStart[0] * 60 + aptStart[1];
        const aptEndMinutes = aptEnd[0] * 60 + aptEnd[1];
        const slotStartMinutes = slotStart[0] * 60 + slotStart[1];
        const slotEndMinutes = slotEnd[0] * 60 + slotEnd[1];
        
        // Check if appointments overlap
        return (aptStartMinutes < slotEndMinutes && aptEndMinutes > slotStartMinutes) &&
               apt.status !== 'cancelled';
      });
      
      setAppointmentsForTimeSlot(matchingAppointments);
    } catch (error) {
      console.error('Error fetching appointments for time slot:', error);
      toast.error('Failed to fetch appointment details');
      setAppointmentsForTimeSlot([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Fetch calendar events
  const fetchEvents = async (start, end) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString()
      });
      
      if (user?.role === 'admin' && selectedTeacher) {
        params.append('teacherId', selectedTeacher);
      }
      
      const response = await api.get(`/calendar/events?${params}`);
      
      setEvents(response.data.events);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Fetch time slots for selected date (admin/teacher) or appointments (parents)
  const fetchTimeSlots = async (date) => {
    try {
      // For parents, fetch appointments for that day instead of time slots
      if (user?.role === 'parent') {
        const dateStr = date.toISOString().split('T')[0];
        const startDate = new Date(dateStr + 'T00:00:00');
        const endDate = new Date(dateStr + 'T23:59:59');
        
        const params = new URLSearchParams({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
        
        const response = await api.get(`/appointments?${params}`);
        const appointments = response.data.appointments || [];
        
        // Convert appointments to time slot format for display
        const appointmentSlots = appointments.map(appointment => ({
          startTime: appointment.startTime,
          endTime: appointment.endTime,
          isBooked: true,
          isAvailable: false,
          status: appointment.status,
          subject: appointment.subject || appointment.primarySubject,
          teacher: appointment.teacher,
          student: appointment.student,
          appointment: appointment
        }));
        
        setTimeSlots(appointmentSlots);
        
        if (appointmentSlots.length === 0) {
          toast.info('No appointments scheduled for this day.');
        }
      } else {
        // For admin/teacher, fetch time slots as before
        const params = new URLSearchParams({
          date: date.toISOString().split('T')[0]
        });
        
        if (user?.role === 'admin' && selectedTeacher) {
          params.append('teacherId', selectedTeacher);
        }
        
        const response = await api.get(`/calendar/timeslots?${params}`);
        
        const timeSlots = response.data.timeSlots;
        setTimeSlots(timeSlots);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
      if (user?.role === 'parent') {
        toast.error('Failed to fetch appointments');
      } else {
        toast.error('Failed to fetch time slots');
      }
    }
  };

  // Handle date selection (for admin/teacher only)
  const handleDateSelect = (selectInfo) => {
    if (user?.role === 'parent') {
      return; // Parents use dayClick instead
    }
    
    setSelectedDate(selectInfo.start);
    setShowTimeSlots(true);
    fetchTimeSlots(selectInfo.start);
    resetForm();
  };

  // Handle day click (for parents to view appointments)
  const handleDayClick = (dayClickInfo) => {
    if (user?.role === 'parent') {
      setSelectedDate(dayClickInfo.date);
      setShowTimeSlots(true);
      fetchTimeSlots(dayClickInfo.date);
    }
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const eventType = clickInfo.event.extendedProps.type;
    const isBookable = clickInfo.event.extendedProps.isBookable;
    
    // Handle appointment events (for parents, schedules are shown as appointments)
    if (eventType === 'appointment') {
      if (user?.role === 'parent') {
        // Check if this is a booked appointment or an available appointment slot
        const appointment = clickInfo.event.extendedProps.appointment;
        const availableSpots = clickInfo.event.extendedProps.availableSpots;
        
        // Show appointment details modal (similar to admin view)
        setSelectedAppointmentEvent(clickInfo.event);
        
        // If it's an available appointment slot (from schedule)
        if (availableSpots !== undefined && availableSpots > 0 && !appointment) {
          setSelectedScheduleSlot(clickInfo.event);
          setSelectedAppointment(null);
          setShowAppointmentDetails(true);
          return;
        }
        
        // If it's a booked appointment
        if (appointment) {
          setSelectedAppointment(appointment);
          setShowAppointmentDetails(true);
          return;
        }
      } else if (user?.role === 'teacher') {
        // For teachers, fetch all appointments for this time slot and show details
        setSelectedAppointmentEvent(clickInfo.event);
        setSelectedAppointment(clickInfo.event.extendedProps.appointment || null);
        fetchAppointmentsForTimeSlot(clickInfo.event);
        setShowAppointmentDetails(true);
        return;
      } else {
        // For admin, show read-only schedule preview (no editing)
        setSelectedScheduleSlot(clickInfo.event);
        setShowSchedulePreview(true);
        return;
      }
    }
    
    // Handle schedule events (only for admin now, teachers don't see schedules)
    if (eventType === 'schedule') {
      if (user?.role === 'admin') {
        // For admin, show schedule preview (view only, no booking)
        setSelectedScheduleSlot(clickInfo.event);
        setShowSchedulePreview(true);
        return;
      }
    }
    
    // Default behavior: do nothing (editing disabled)
    return;
  };

  // Create new event
  const handleCreateEvent = async () => {
    try {
      const teacherId = user?.role === 'teacher' ? user._id : formData.teacherId;
      
      if (!teacherId) {
        toast.error('Please select a teacher');
        return;
      }
      
      const response = await api.post('/calendar/events', {
        ...formData,
        teacherId
      });
      
      setEvents(prev => [...prev, response.data.event]);
      setShowModal(false);
      setShowTimeSlots(false);
      resetForm();
      toast.success('Event created successfully');
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.message || 'Failed to create event');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      start: '',
      end: '',
      studentId: '',
      subject: '',
      location: 'in-person',
      notes: '',
      teacherId: ''
    });
    setSelectedEvent(null);
  };

  // Handle booking schedule slot
  const handleBookScheduleSlot = async () => {
    if (!bookingData.studentId) {
      toast.error('Please select a student');
      return;
    }

    // Automatically get subject from the schedule/appointment slot
    const subject = selectedScheduleSlot.extendedProps.primarySubject || 
                   selectedScheduleSlot.extendedProps.subjects?.[0] || 
                   selectedScheduleSlot.extendedProps.subject || '';

    try {
      const response = await api.post('/calendar/book-schedule-slot', {
        scheduleId: selectedScheduleSlot.extendedProps.scheduleId,
        studentId: bookingData.studentId,
        scheduledDate: selectedScheduleSlot.start.toISOString(),
        slotStart: selectedScheduleSlot.extendedProps.slotStart,
        slotEnd: selectedScheduleSlot.extendedProps.slotEnd,
        subject: subject,
        notes: bookingData.notes
      });

      // Refresh events to show the new appointment
      const calendarApi = calendarRef.current.getApi();
      fetchEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);

      setShowBookingModal(false);
      setBookingData({ studentId: '', notes: '' });
      toast.success('Appointment booked successfully!');
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to book appointment');
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await api.put(`/appointments/${selectedAppointment._id}/cancel`);
      toast.success('Appointment cancelled successfully');
      setShowCancelModal(false);
      setSelectedAppointment(null);
      setCancellationLimit(null);
      
      // Refresh calendar events
      const calendarApi = calendarRef.current.getApi();
      fetchEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      if (error.response?.data?.limitReached) {
        // Update cancellation limit state if limit was reached
        setCancellationLimit({
          canCancel: false,
          currentCount: error.response.data.currentCount,
          limit: error.response.data.limit,
          month: error.response.data.month
        });
      }
      toast.error(error.response?.data?.message || 'Failed to cancel appointment');
    }
  };

  // Fetch cancellation limit when opening cancel modal for parents
  const handleOpenCancelModal = async (appointment) => {
    setSelectedAppointment(appointment);
    
    // Check cancellation limit for parents
    if (user?.role === 'parent') {
      try {
        const response = await api.get('/appointments/cancellation-limit');
        setCancellationLimit(response.data);
      } catch (error) {
        console.error('Error checking cancellation limit:', error);
        // Continue anyway, will show error if limit exceeded when trying to cancel
      }
    }
    
    setShowCancelModal(true);
  };

  const handleRescheduleAppointment = async () => {
    setShowCancelModal(false);
    
    if (!selectedAppointment) return;
    
    // Check reschedule limit for parents
    if (user?.role === 'parent') {
      try {
        const response = await api.get('/appointments/reschedule-limit');
        setRescheduleLimit(response.data);
        
        // Fetch available reschedule options
        setLoadingRescheduleOptions(true);
        const optionsResponse = await api.get(`/appointments/${selectedAppointment._id}/reschedule-options`);
        setRescheduleOptions(optionsResponse.data.options || []);
      } catch (error) {
        console.error('Error checking reschedule limit or fetching options:', error);
        toast.error('Failed to load reschedule options');
      } finally {
        setLoadingRescheduleOptions(false);
      }
    }
    
    setShowRescheduleModal(true);
  };

  const confirmRescheduleAppointment = async () => {
    if (!selectedAppointment) return;

    // Check if limit exceeded and we need to submit a request instead
    if (user?.role === 'parent' && rescheduleLimit && !rescheduleLimit.canReschedule) {
      // Submit reschedule request
      if (!rescheduleReason || rescheduleReason.length < 10) {
        toast.error('Please provide a reason (at least 10 characters) for the reschedule request');
        return;
      }

      try {
        const newDate = selectedRescheduleOption ? selectedRescheduleOption.dateString : undefined;
        const newStartTime = selectedRescheduleOption ? selectedRescheduleOption.startTime : undefined;
        const newEndTime = selectedRescheduleOption ? selectedRescheduleOption.endTime : undefined;

        await api.post(`/appointments/${selectedAppointment._id}/reschedule-request`, {
          reason: rescheduleReason,
          newDate: newDate,
          newStartTime: newStartTime,
          newEndTime: newEndTime
        });

        toast.success('Reschedule request submitted. Admin will be notified.');
        setShowRescheduleModal(false);
        setSelectedAppointment(null);
        setRescheduleReason('');
        setRescheduleLimit(null);

        // Refresh calendar events
        const calendarApi = calendarRef.current.getApi();
        fetchEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
      } catch (error) {
        console.error('Error submitting reschedule request:', error);
        toast.error(error.response?.data?.message || 'Failed to submit reschedule request');
      }
      return;
    }

    // Direct reschedule (within limit)
    if (!selectedRescheduleOption) {
      toast.error('Please select an available appointment time');
      return;
    }

    try {
      await api.put(`/appointments/${selectedAppointment._id}/reschedule`, {
        scheduleId: selectedRescheduleOption.scheduleId,
        dateString: selectedRescheduleOption.dateString,
        startTime: selectedRescheduleOption.startTime,
        endTime: selectedRescheduleOption.endTime
      });
      toast.success('Appointment rescheduled successfully');
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
      setRescheduleLimit(null);
      setRescheduleOptions([]);
      setSelectedRescheduleOption(null);
      
      // Refresh calendar events
      const calendarApi = calendarRef.current.getApi();
      fetchEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      if (error.response?.data?.limitExceeded) {
        // If limit was exceeded during reschedule, update state and show request form
        setRescheduleLimit({
          canReschedule: false,
          currentCount: error.response.data.currentCount,
          limit: error.response.data.limit,
          quarter: error.response.data.quarter
        });
        toast.error(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle time slot click (admin only)
  const handleTimeSlotClick = (slot) => {
    // Only allow admins to create events
    if (user?.role !== 'admin') {
      return;
    }
    
    if (slot.isBooked) {
      toast.error('This time slot is already booked');
      return;
    }
    
    const startDate = new Date(selectedDate);
    const [startHour, startMinute] = slot.startTime.split(':').map(Number);
    const [endHour, endMinute] = slot.endTime.split(':').map(Number);
    
    startDate.setHours(startHour, startMinute, 0, 0);
    const endDate = new Date(startDate);
    endDate.setHours(endHour, endMinute, 0, 0);
    
    setFormData(prev => ({
      ...prev,
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      title: prev.subject || 'New Appointment'
    }));
    
    setShowModal(true);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <CalendarIcon className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
            <p className="text-sm text-gray-600">
              {user?.role === 'parent' 
                ? 'View your children\'s appointments' 
                : 'Manage appointments and schedules'
              }
            </p>
          </div>
        </div>
        
        {/* Teacher Filter for Admin */}
        {user?.role === 'admin' && (
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Teacher:</label>
            <select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Teachers</option>
              {teachers.map(teacher => (
                <option key={teacher._id} value={teacher._id}>
                  {teacher.firstName} {teacher.lastName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-center justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-primary-500 rounded"></div>
            <span className="text-gray-700">Booked Appointments</span>
          </div>
          {user?.role !== 'parent' && (
            <>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded border-2 border-dashed border-green-500 opacity-70"></div>
                <span className="text-gray-700">Available Time Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-success-500 rounded"></div>
                <span className="text-gray-700">Available Appointments</span>
              </div>
            </>
          )}
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-warning-500 rounded"></div>
            <span className="text-gray-700">In Progress</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-secondary-500 rounded"></div>
            <span className="text-gray-700">Completed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-danger-500 rounded"></div>
            <span className="text-gray-700">Cancelled</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 bg-white">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          editable={false}
          selectable={user?.role === 'admin'}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          events={events}
          eventClick={handleEventClick}
          select={handleDateSelect}
          dayClick={handleDayClick}
          datesSet={(dateInfo) => fetchEvents(dateInfo.start, dateInfo.end)}
          height="100%"
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          eventDisplay="block"
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }}
          // Site-themed styling
          dayHeaderFormat={{ weekday: 'short' }}
          dayMaxEventRows={3}
          moreLinkClick="popover"
          eventBorderColor="transparent"
          eventBackgroundColor="#3b82f6"
          eventTextColor="white"
          // Better spacing and sizing
          aspectRatio={1.8}
          expandRows={true}
          // Site colors
          eventColor="#3b82f6"
          // Better event rendering
          eventClassNames="cursor-pointer hover:opacity-80"
          // Improved day cell styling
          dayCellClassNames="hover:bg-gray-50"
          // Better time grid styling
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }}
        />
      </div>

      {/* Time Slots Panel */}
      {showTimeSlots && (
        <div className="bg-white rounded-lg shadow p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {user?.role === 'parent' 
                ? `Your Children's Appointments - ${selectedDate?.toLocaleDateString()}`
                : `Available Time Slots - ${selectedDate?.toLocaleDateString()}`
              }
            </h3>
            <button
              onClick={() => setShowTimeSlots(false)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {timeSlots.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                {user?.role === 'parent' 
                  ? 'No Appointments Scheduled' 
                  : 'No Available Time Slots'
                }
              </h4>
              <p className="text-gray-500 mb-4">
                {user?.role === 'parent' 
                  ? "There are no available time slots for this day. Please try selecting a different date or contact the administrator for more information."
                  : "No time slots are available for this day. Teachers may not have scheduled availability for this date."
                }
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowTimeSlots(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  Close
                </button>
                {user?.role === 'parent' && (
                  <button
                    onClick={() => {
                      // Navigate to a different view or show contact info
                      toast.info('Please contact the administrator for assistance with scheduling.');
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Contact Admin
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {user?.role === 'parent' ? (
                // For parents: Show detailed appointment list (read-only)
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {timeSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">
                              {slot.startTime} - {slot.endTime}
                            </span>
                            {slot.status && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                slot.status === 'booked' ? 'bg-blue-100 text-blue-800' :
                                slot.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                slot.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                                slot.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {slot.status.charAt(0).toUpperCase() + slot.status.slice(1)}
                              </span>
                            )}
                          </div>
                          {slot.subject && (
                            <div className="flex items-center space-x-2 mb-1">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-700">
                                {slot.subject}
                              </span>
                            </div>
                          )}
                          {slot.teacher && (
                            <div className="flex items-center space-x-2 mb-1">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-700">
                                {slot.teacher.firstName} {slot.teacher.lastName}
                              </span>
                            </div>
                          )}
                          {slot.student && (
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Student: {slot.student.firstName} {slot.student.lastName}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // For admin/teacher: Show clickable time slots for creating events
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-60 overflow-y-auto time-slots-container">
                  {timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      onClick={() => handleTimeSlotClick(slot)}
                      disabled={slot.isBooked}
                      className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                        slot.isBooked
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold">{slot.startTime}</div>
                        <div className="text-xs text-gray-500">{slot.endTime}</div>
                        {slot.isBooked && (
                          <div className="text-xs text-red-500 mt-1">Booked</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Event Modal - Only for admin */}
      {showModal && user?.role === 'admin' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Create Event
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter event title"
                />
              </div>
              
              {/* Teacher (Admin only) */}
              {user?.role === 'admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher *
                  </label>
                  <select
                    name="teacherId"
                    value={formData.teacherId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map(teacher => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.firstName} {teacher.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter subject"
                />
              </div>
              
              {/* Start Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  name="start"
                  value={formData.start ? new Date(formData.start).toISOString().slice(0, 16) : ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              {/* End Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time *
                </label>
                <input
                  type="datetime-local"
                  name="end"
                  value={formData.end ? new Date(formData.end).toISOString().slice(0, 16) : ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="in-person">In-Person</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter notes"
                />
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEvent}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  <Save className="h-4 w-4" />
                  <span>Create</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Preview Modal */}
      {showSchedulePreview && selectedScheduleSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Time Slot Details</h3>
              <button
                onClick={() => {
                  setShowSchedulePreview(false);
                  setSelectedScheduleSlot(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Time Information */}
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedScheduleSlot.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {selectedScheduleSlot.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedScheduleSlot.start.toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {/* Teacher Information */}
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedScheduleSlot.extendedProps.teacher?.firstName} {selectedScheduleSlot.extendedProps.teacher?.lastName}
                  </p>
                  <p className="text-xs text-gray-500">Teacher</p>
                </div>
              </div>
              
              {/* Subject Information */}
              <div className="flex items-start space-x-3">
                <FileText className="h-5 w-5 text-primary-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-2">Subject Information</p>
                  <div className="space-y-2">
                    {selectedScheduleSlot.extendedProps.primarySubject && (
                      <div>
                        <span className="text-xs text-gray-500 font-medium">Primary Subject:</span>
                        <div className="mt-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            selectedScheduleSlot.extendedProps.primarySubject === 'abacus' ? 'bg-yellow-100 text-yellow-800' :
                            selectedScheduleSlot.extendedProps.primarySubject === 'math' ? 'bg-blue-100 text-blue-800' :
                            selectedScheduleSlot.extendedProps.primarySubject === 'english' ? 'bg-green-100 text-green-800' :
                            selectedScheduleSlot.extendedProps.primarySubject === 'coding' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedScheduleSlot.extendedProps.primarySubject.charAt(0).toUpperCase() + selectedScheduleSlot.extendedProps.primarySubject.slice(1)}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedScheduleSlot.extendedProps.subjects && selectedScheduleSlot.extendedProps.subjects.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500 font-medium">All Subjects:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedScheduleSlot.extendedProps.subjects.map((subject, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {subject}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {!selectedScheduleSlot.extendedProps.primarySubject && (!selectedScheduleSlot.extendedProps.subjects || selectedScheduleSlot.extendedProps.subjects.length === 0) && (
                      <span className="text-xs text-gray-500 italic">No subjects specified</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Schedule Details */}
              <div className="flex items-center space-x-3">
                <CalendarIcon className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Day of Week</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {selectedScheduleSlot.extendedProps.dayOfWeek || 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Duration */}
              {selectedScheduleSlot.extendedProps.duration && (
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-primary-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Duration</p>
                    <p className="text-xs text-gray-500">
                      {selectedScheduleSlot.extendedProps.duration} minutes
                    </p>
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedScheduleSlot.extendedProps.notes && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Notes:</p>
                  <p className="text-xs text-gray-600">{selectedScheduleSlot.extendedProps.notes}</p>
                </div>
              )}
              
              {/* Capacity Information */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Capacity</span>
                  <span className="text-sm text-gray-900">
                    {selectedScheduleSlot.extendedProps.currentBookings || 0} / {selectedScheduleSlot.extendedProps.maxStudents || 1}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${((selectedScheduleSlot.extendedProps.currentBookings || 0) / (selectedScheduleSlot.extendedProps.maxStudents || 1)) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
              
              {/* Status */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-700">Available for booking</span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowSchedulePreview(false);
                  setSelectedScheduleSlot(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal for Parents */}
      {showBookingModal && selectedScheduleSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Book Available Appointment</h3>
                <button
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedScheduleSlot(null);
                    setBookingData({ studentId: '', notes: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Appointment Details */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CalendarIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedScheduleSlot.start.toLocaleDateString()} at {selectedScheduleSlot.extendedProps.slotStart ? formatTimeTo12Hour(selectedScheduleSlot.extendedProps.slotStart) : selectedScheduleSlot.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    Teacher: {selectedScheduleSlot.extendedProps.teacher?.firstName} {selectedScheduleSlot.extendedProps.teacher?.lastName}
                  </span>
                </div>
                {/* Show available spots */}
                {selectedScheduleSlot.extendedProps.availableSpots !== undefined && (
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-700">
                      {selectedScheduleSlot.extendedProps.availableSpots} spot{selectedScheduleSlot.extendedProps.availableSpots !== 1 ? 's' : ''} available
                      {selectedScheduleSlot.extendedProps.maxStudents && (
                        <span className="text-gray-500 ml-1">
                          (of {selectedScheduleSlot.extendedProps.maxStudents} total)
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <div className="flex flex-wrap gap-1">
                    {selectedScheduleSlot.extendedProps.primarySubject && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        selectedScheduleSlot.extendedProps.primarySubject === 'abacus' ? 'bg-yellow-100 text-yellow-800' :
                        selectedScheduleSlot.extendedProps.primarySubject === 'math' ? 'bg-blue-100 text-blue-800' :
                        selectedScheduleSlot.extendedProps.primarySubject === 'english' ? 'bg-green-100 text-green-800' :
                        selectedScheduleSlot.extendedProps.primarySubject === 'coding' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedScheduleSlot.extendedProps.primarySubject.charAt(0).toUpperCase() + selectedScheduleSlot.extendedProps.primarySubject.slice(1)}
                      </span>
                    )}
                    {selectedScheduleSlot.extendedProps.subjects && selectedScheduleSlot.extendedProps.subjects.filter(s => s !== selectedScheduleSlot.extendedProps.primarySubject).map((subject, index) => (
                      <span key={index} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Booking Form */}
              <div className="space-y-4">
                {/* Student Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Student *
                  </label>
                  <select
                    value={bookingData.studentId}
                    onChange={(e) => setBookingData(prev => ({ ...prev, studentId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Choose a student</option>
                    {students.map(student => (
                      <option key={student._id} value={student._id}>
                        {student.firstName} {student.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={bookingData.notes}
                    onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="Any additional notes (optional)"
                  />
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedScheduleSlot(null);
                  setBookingData({ studentId: '', notes: '' });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBookScheduleSlot}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Modal */}
      {showCancelModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment</h3>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedAppointment(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Show cancellation limit status for parents */}
              {user?.role === 'parent' && cancellationLimit && (
                <div className={`border rounded-lg p-4 mb-4 ${
                  cancellationLimit.canCancel 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 mr-2 ${
                      cancellationLimit.canCancel 
                        ? 'text-blue-600' 
                        : 'text-yellow-600'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        cancellationLimit.canCancel 
                          ? 'text-blue-800' 
                          : 'text-yellow-800'
                      }`}>
                        {cancellationLimit.canCancel 
                          ? `Cancellation Token: ${cancellationLimit.currentCount} of ${cancellationLimit.limit} used (${cancellationLimit.month})`
                          : 'Cancellation Limit Reached - Contact Admin Required'
                        }
                      </p>
                      <p className={`text-sm mt-1 ${
                        cancellationLimit.canCancel 
                          ? 'text-blue-700' 
                          : 'text-yellow-700'
                      }`}>
                        {cancellationLimit.canCancel 
                          ? `You have ${cancellationLimit.limit - cancellationLimit.currentCount} cancellation token${cancellationLimit.limit - cancellationLimit.currentCount !== 1 ? 's' : ''} remaining this month.`
                          : `You have used all ${cancellationLimit.limit} cancellation token${cancellationLimit.limit !== 1 ? 's' : ''} for this month (${cancellationLimit.month}). You must contact the admin to cancel additional appointments.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3 mb-4">
                <AlertTriangle className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Are you sure you want to cancel this appointment?</p>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Appointment Details:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Date:</strong> {new Date(selectedAppointment.scheduledDate).toLocaleDateString()}</p>
                  <p><strong>Time:</strong> {selectedAppointment.startTime} - {selectedAppointment.endTime}</p>
                  <p><strong>Subject:</strong> {selectedAppointment.subject}</p>
                  <p><strong>Teacher:</strong> {selectedAppointment.teacher?.firstName} {selectedAppointment.teacher?.lastName}</p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedAppointment(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  Keep Appointment
                </button>
                <button
                  onClick={handleCancelAppointment}
                  disabled={user?.role === 'parent' && cancellationLimit && !cancellationLimit.canCancel}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel Appointment
                </button>
              </div>

              <div className="mt-3">
                <button
                  onClick={handleRescheduleAppointment}
                  className="w-full px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                >
                  Reschedule Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Details Modal for Parents and Teachers */}
      {showAppointmentDetails && selectedAppointmentEvent && (user?.role === 'parent' || user?.role === 'teacher') && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* For teachers: Show appointment details with all bookings */}
            {user?.role === 'teacher' ? (
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Appointment Details</h3>
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setAppointmentsForTimeSlot([]);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  {/* Time Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock className="h-5 w-5 text-primary-600" />
                      <h4 className="text-sm font-medium text-gray-900">Time Slot</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Date:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAppointmentEvent.start.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Time:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAppointmentEvent.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {selectedAppointmentEvent.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Subject:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedAppointmentEvent.extendedProps.primarySubject 
                            ? selectedAppointmentEvent.extendedProps.primarySubject.charAt(0).toUpperCase() + selectedAppointmentEvent.extendedProps.primarySubject.slice(1)
                            : selectedAppointmentEvent.extendedProps.subject || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bookings Information */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Users className="h-5 w-5 text-primary-600" />
                        <h4 className="text-sm font-medium text-gray-900">Bookings</h4>
                      </div>
                      <span className="text-sm font-semibold text-primary-600">
                        {loadingAppointments ? 'Loading...' : `${appointmentsForTimeSlot.length} ${appointmentsForTimeSlot.length === 1 ? 'student' : 'students'}`}
                      </span>
                    </div>
                    
                    {loadingAppointments ? (
                      <div className="text-center py-4">
                        <div className="loading-spinner h-6 w-6 mx-auto"></div>
                        <p className="text-xs text-gray-500 mt-2">Loading appointments...</p>
                      </div>
                    ) : appointmentsForTimeSlot.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500">No students have booked this time slot yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {appointmentsForTimeSlot.map((appointment, index) => (
                          <div key={appointment._id || index} className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <User className="h-4 w-4 text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {appointment.student?.firstName} {appointment.student?.lastName}
                                  </p>
                                  {appointment.student?.grade && (
                                    <p className="text-xs text-gray-500">
                                      Grade {appointment.student.grade}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                appointment.status === 'booked' ? 'bg-blue-100 text-blue-800' :
                                appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                appointment.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                                appointment.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {appointment.status?.charAt(0).toUpperCase() + appointment.status?.slice(1) || 'Booked'}
                              </span>
                            </div>
                            {appointment.notes && (
                              <p className="text-xs text-gray-600 mt-2 italic">
                                {appointment.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="p-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setAppointmentsForTimeSlot([]);
                    }}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : selectedAppointment && user?.role === 'parent' ? (
              /* For booked appointments: Show simple popup with Reschedule/Cancel options */
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Booked Appointment</h3>
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setSelectedScheduleSlot(null);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Brief appointment summary */}
                <div className="p-6 space-y-3">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Date:</span>
                      <span className="text-sm text-gray-900">
                        {selectedAppointmentEvent.start.toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Time:</span>
                      <span className="text-sm text-gray-900">
                        {selectedAppointmentEvent.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {selectedAppointmentEvent.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Subject:</span>
                      <span className="text-sm text-gray-900">
                        {selectedAppointmentEvent.extendedProps.primarySubject 
                          ? selectedAppointmentEvent.extendedProps.primarySubject.charAt(0).toUpperCase() + selectedAppointmentEvent.extendedProps.primarySubject.slice(1)
                          : selectedAppointmentEvent.extendedProps.subject || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Teacher:</span>
                      <span className="text-sm text-gray-900">
                        {selectedAppointmentEvent.extendedProps.teacher?.firstName} {selectedAppointmentEvent.extendedProps.teacher?.lastName}
                      </span>
                    </div>
                    {selectedAppointment && selectedAppointment.student && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Student:</span>
                        <span className="text-sm text-gray-900">
                          {selectedAppointment.student.firstName} {selectedAppointment.student.lastName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="p-6 border-t border-gray-200 space-y-3">
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      handleRescheduleAppointment();
                    }}
                    className="w-full px-4 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-medium"
                  >
                    Reschedule Appointment
                  </button>
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      handleOpenCancelModal(selectedAppointment);
                    }}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                  >
                    Cancel Appointment
                  </button>
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setSelectedScheduleSlot(null);
                    }}
                    className="w-full px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              /* For available appointment slots: Show booking modal */
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Appointment Details</h3>
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setSelectedScheduleSlot(null);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {selectedAppointmentEvent.extendedProps.primarySubject && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          selectedAppointmentEvent.extendedProps.primarySubject === 'abacus' ? 'bg-yellow-100 text-yellow-800' :
                          selectedAppointmentEvent.extendedProps.primarySubject === 'math' ? 'bg-blue-100 text-blue-800' :
                          selectedAppointmentEvent.extendedProps.primarySubject === 'english' ? 'bg-green-100 text-green-800' :
                          selectedAppointmentEvent.extendedProps.primarySubject === 'coding' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedAppointmentEvent.extendedProps.primarySubject.charAt(0).toUpperCase() + selectedAppointmentEvent.extendedProps.primarySubject.slice(1)}
                        </span>
                      )}
                      {selectedAppointmentEvent.extendedProps.subjects?.filter(s => s !== selectedAppointmentEvent.extendedProps.primarySubject).map((subject, index) => (
                        <span key={index} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                          {subject}
                        </span>
                      ))}
                      {selectedAppointmentEvent.extendedProps.subject && !selectedAppointmentEvent.extendedProps.primarySubject && (
                        <span className="text-sm text-gray-900">{selectedAppointmentEvent.extendedProps.subject}</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedAppointmentEvent.start.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  
                  {/* Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedAppointmentEvent.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {selectedAppointmentEvent.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  
                  {/* Teacher */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teacher
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedAppointmentEvent.extendedProps.teacher?.firstName} {selectedAppointmentEvent.extendedProps.teacher?.lastName}
                    </p>
                  </div>
                  
                  {/* Available Spots */}
                  {selectedAppointmentEvent.extendedProps.availableSpots !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Availability
                      </label>
                      <p className="text-sm font-medium text-green-700">
                        {selectedAppointmentEvent.extendedProps.availableSpots} spot{selectedAppointmentEvent.extendedProps.availableSpots !== 1 ? 's' : ''} available
                        {selectedAppointmentEvent.extendedProps.maxStudents && (
                          <span className="text-gray-500 ml-1">
                            (of {selectedAppointmentEvent.extendedProps.maxStudents} total)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAppointmentDetails(false);
                      setSelectedAppointmentEvent(null);
                      setSelectedAppointment(null);
                      setSelectedScheduleSlot(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    Close
                  </button>
                  
                  {/* For available appointment slots - show Book button */}
                  {selectedAppointmentEvent.extendedProps.availableSpots !== undefined && 
                   selectedAppointmentEvent.extendedProps.availableSpots > 0 && 
                   !selectedAppointment && (
                    <button
                      onClick={() => {
                        setShowAppointmentDetails(false);
                        setSelectedScheduleSlot(selectedAppointmentEvent);
                        setBookingData({
                          studentId: '',
                          notes: ''
                        });
                        setShowBookingModal(true);
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                      Book Appointment
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reschedule Appointment Modal */}
      {showRescheduleModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Reschedule Appointment</h3>
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setSelectedAppointment(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Appointment:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Date:</strong> {new Date(selectedAppointment.scheduledDate).toLocaleDateString()}</p>
                  <p><strong>Time:</strong> {selectedAppointment.startTime} - {selectedAppointment.endTime}</p>
                  <p><strong>Subject:</strong> {selectedAppointment.subject}</p>
                  <p><strong>Teacher:</strong> {selectedAppointment.teacher?.firstName} {selectedAppointment.teacher?.lastName}</p>
                </div>
              </div>

              {/* Show reschedule limit status */}
              {user?.role === 'parent' && rescheduleLimit && (
                <div className={`border rounded-lg p-4 mb-4 ${
                  rescheduleLimit.canReschedule 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 mr-2 ${
                      rescheduleLimit.canReschedule 
                        ? 'text-blue-600' 
                        : 'text-yellow-600'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        rescheduleLimit.canReschedule 
                          ? 'text-blue-800' 
                          : 'text-yellow-800'
                      }`}>
                        {rescheduleLimit.canReschedule 
                          ? `Reschedule Tokens: ${rescheduleLimit.currentCount} of ${rescheduleLimit.limit} used (${rescheduleLimit.quarter})`
                          : 'Reschedule Limit Reached - Contact Admin Required'
                        }
                      </p>
                      <p className={`text-sm mt-1 ${
                        rescheduleLimit.canReschedule 
                          ? 'text-blue-700' 
                          : 'text-yellow-700'
                      }`}>
                        {rescheduleLimit.canReschedule 
                          ? `You have ${rescheduleLimit.limit - rescheduleLimit.currentCount} reschedule token${rescheduleLimit.limit - rescheduleLimit.currentCount !== 1 ? 's' : ''} remaining this quarter.`
                          : `You have used all ${rescheduleLimit.limit} reschedule tokens for this quarter (${rescheduleLimit.quarter}). You must contact the admin to request a reschedule. Please submit a reschedule request below.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* For parents: Show available appointments list */}
              {user?.role === 'parent' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {rescheduleLimit && !rescheduleLimit.canReschedule 
                        ? 'Select Preferred Appointment (Optional - Admin will review)' 
                        : 'Select New Appointment Time from Available Appointments *'}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Choose from existing available appointment slots that match your student's class ({selectedAppointment?.primarySubject || 'N/A'}). Only appointments within the next 2 weeks are shown.
                    </p>
                    
                    {loadingRescheduleOptions ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">Loading available appointments...</p>
                      </div>
                    ) : rescheduleOptions.length === 0 ? (
                      <div className="text-center py-8 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-500">No available appointments found for the next 2 weeks.</p>
                        <p className="text-xs text-gray-400 mt-1">Please contact admin for assistance.</p>
                      </div>
                    ) : (
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        {rescheduleOptions.map((option, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSelectedRescheduleOption(option)}
                            className={`w-full text-left p-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                              selectedRescheduleOption?.scheduleId === option.scheduleId &&
                              selectedRescheduleOption?.dateString === option.dateString &&
                              selectedRescheduleOption?.startTime === option.startTime
                                ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                                : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {new Date(option.date).toLocaleDateString('en-US', { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {option.startTime} - {option.endTime}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {option.teacher.firstName} {option.teacher.lastName}
                                </p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-green-600 font-medium">
                                  {option.availableSpots} spot{option.availableSpots !== 1 ? 's' : ''} available
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Reason field for reschedule requests (required when limit exceeded) */}
                  {rescheduleLimit && !rescheduleLimit.canReschedule && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Reschedule Request *
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Since you have used all your reschedule tokens, you must contact the admin. Please provide a detailed reason for your reschedule request.
                      </p>
                      <textarea
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        placeholder="Please explain why you need to reschedule this appointment. The admin will review your request and contact you..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={4}
                        required
                        minLength={10}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 10 characters required. Admin will be notified and will review your request.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* For admin/teacher: Keep original date/time picker form */
                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Date *
                      </label>
                      <input
                        type="date"
                        name="start"
                        value={formData.start ? formData.start.split('T')[0] : ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          start: e.target.value + 'T' + (prev.start ? prev.start.split('T')[1] : '09:00:00'),
                          end: e.target.value + 'T' + (prev.end ? prev.end.split('T')[1] : '10:00:00')
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time *
                        </label>
                        <input
                          type="time"
                          name="startTime"
                          value={formData.start ? formData.start.split('T')[1].substring(0, 5) : ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            start: (prev.start ? prev.start.split('T')[0] : new Date().toISOString().split('T')[0]) + 'T' + e.target.value + ':00'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time *
                        </label>
                        <input
                          type="time"
                          name="endTime"
                          value={formData.end ? formData.end.split('T')[1].substring(0, 5) : ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            end: (prev.end ? prev.end.split('T')[0] : new Date().toISOString().split('T')[0]) + 'T' + e.target.value + ':00'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </form>
              )}

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowRescheduleModal(false);
                    setSelectedAppointment(null);
                    setRescheduleOptions([]);
                    setSelectedRescheduleOption(null);
                    setRescheduleReason('');
                    setRescheduleLimit(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRescheduleAppointment}
                  disabled={
                    user?.role === 'parent' && (
                      (rescheduleLimit?.canReschedule && !selectedRescheduleOption) ||
                      (!rescheduleLimit?.canReschedule && !rescheduleReason)
                    )
                  }
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {user?.role === 'parent' && rescheduleLimit && !rescheduleLimit.canReschedule 
                    ? 'Submit Request' 
                    : 'Reschedule Appointment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
